/**
 * PATH: packages/ui/src/components/mobile-dock/MobileDock.tsx
 * FILE_ID: UI-SHELL-MOBILE-DOCK-VIEW-001
 * PURPOSE: The one bottom navigation strip, shared by Jobs and every hub.
 * WHY: A second strip was stacked under Report, Teams, and AI Chat.
 * NON-RESPONSIBILITIES: Does not read a hub nav config. The caller passes items.
 * NOTES FOR FUTURE AI: Keep the 1023.98px gate in lockstep with BottomDock.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useSortedByActionUsage } from '../../lib/actionUsageSort'
import { trackNavClick } from '../../lib/navTracking'
import { useEdgeSnapBarDrag } from '../../mobile/useEdgeSnapDrag'
import type { HubId } from '../types'
import {
  MobileDockCollapseControl,
  MobileDockRestoreControl,
  useRestoreCornerDrag,
} from './MobileDockCollapseControls'
import { MOBILE_DOCK_SHELL, mobileDockTileClasses } from './mobileDockChrome'
import {
  isDockItemActive,
  isExternalHref,
  type DockItem,
  type DockItemKind,
} from './mobileDockItems'
import { useMobileDockScroll } from './useMobileDockScroll'
import { useMobileDockChromeState } from './useMobileDockChromeState'

const NARROW_QUERY = '(max-width: 1023.98px)'

export interface MobileDockProps {
  items: DockItem[]
  /** Render the dock on desktop too (billing hub reclaims sidebar width). */
  forceVisible?: boolean
  /** Usage-sort bucket. Jobs keeps its historical key. */
  storageKey?: string
  /** Drag-edge persistence key. Jobs keeps its historical key. */
  edgeKey?: string
  /** Hub id recorded for nav and utility taps. Hub tiles use their own id. */
  usageHubId?: HubId
}

function dockNavHubId(kind: DockItemKind, id: string, fallback: HubId): HubId {
  return kind === 'hub' ? (id as HubId) : fallback
}

function useIsNarrowViewport(): boolean {
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(NARROW_QUERY).matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(NARROW_QUERY)
    setIsNarrow(mql.matches)
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches)
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [])
  return isNarrow
}

function isActive(currentPath: string, item: DockItem): boolean {
  return isDockItemActive(currentPath, item)
}

/** Status → People accent classes. Teal washes read as blue on master and
 *  are rejected (operator 2026-08-12). See mobileDockChrome.ts. */
function statusClasses(item: DockItem, active: boolean): string {
  return mobileDockTileClasses(item.kind, item.status, active)
}

/**
 * Bottom navigation. Visible below the lg breakpoint, or when forceVisible
 * is set. Clears --mobile-dock-h when it is not on screen.
 */
export function MobileDock({
  items,
  forceVisible = false,
  storageKey = 'quotech.mobile-dock',
  edgeKey = 'quotech.mobile-dock.edge',
  usageHubId = 'jobs',
}: MobileDockProps) {
  const isNarrow = useIsNarrowViewport()
  const shouldShow = isNarrow || forceVisible
  const location = useLocation()
  const dockRef = useRef<HTMLDivElement>(null)

  const { sorted: dockItems, onActionUsed: trackDockNav } = useSortedByActionUsage(
    storageKey,
    items,
    (item) => item.href || item.id,
  )
  const {
    scrollRef,
    canScrollLeft,
    canScrollRight,
    scrollDock,
  } = useMobileDockScroll(dockItems.length, shouldShow, location.pathname)
  const dockDrag = useEdgeSnapBarDrag(edgeKey, 'bottom')
  const dockAtTop = dockDrag.edge === 'top'
  // The restore pill's corner lives here (not inside the control) so the
  // collapsed floor reserve below can be corner-aware.
  const restoreDrag = useRestoreCornerDrag()
  const restoreAtBottom = restoreDrag.corner === 'bl' || restoreDrag.corner === 'br'
  const { collapsed, collapse, restore, compactRestore, restoreHosted } = useMobileDockChromeState({
    shouldShow,
    dockAtTop,
    restoreAtBottom,
  })

  if (!shouldShow) return null

  return (
    <>
      <MobileDockRestoreControl
        collapsed={collapsed && !restoreHosted}
        onRestore={restore}
        drag={restoreDrag}
        compact={compactRestore}
      />

      {/* The dock itself. Slides down out of view (and stops catching taps)
          when collapsed. */}
      <div
        className={cn(
          'fixed inset-x-0 z-30 pointer-events-none',
          dockAtTop ? 'top-0' : 'bottom-0',
          'transition-transform duration-300 ease-out',
          collapsed && (dockAtTop ? '-translate-y-[130%]' : 'translate-y-[130%]'),
        )}
        role="navigation"
        aria-label="Hub dock"
        aria-hidden={collapsed}
        data-master-mobile-dock="true"
        data-collapsed={collapsed ? 'true' : 'false'}
        data-edge={dockDrag.edge}
      >
      <div
        ref={dockRef}
        className={cn(
          // A collapsed strip still painted on top of the Menu pill and
          // swallowed the tap. It is off-screen; it must not take clicks.
          collapsed ? 'pointer-events-none' : 'pointer-events-auto',
          'mx-2',
          'relative',
          'flex items-stretch',
          MOBILE_DOCK_SHELL,
          'overflow-hidden',
        )}
        // Lift the pill above the iOS home indicator / Android gesture bar.
        // Falls back to 0.5rem when there's no inset (or before V1's
        // viewport-fit=cover lands).
        style={{ marginBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          data-testid="master-mobile-dock-move"
          aria-label="Move navigation dock"
          title="Drag to move the dock"
          className="flex-shrink-0 flex items-center justify-center w-8 my-1.5 ml-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors touch-none"
          {...dockDrag.bind}
        >
          <span className="grid grid-cols-2 gap-0.5" aria-hidden>
            <span className="h-1 w-1 rounded-full bg-current" />
            <span className="h-1 w-1 rounded-full bg-current" />
            <span className="h-1 w-1 rounded-full bg-current" />
            <span className="h-1 w-1 rounded-full bg-current" />
          </span>
        </button>
        <MobileDockCollapseControl onCollapse={collapse} />
        {canScrollLeft && (
          <button
            type="button"
            data-testid="master-mobile-dock-scroll-left"
            onClick={() => scrollDock(-1)}
            aria-label="Scroll navigation left"
            className="flex-shrink-0 flex items-center justify-center w-8 my-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        <div
          ref={scrollRef}
          data-testid="master-mobile-dock-scroll"
          className={cn(
            'flex items-stretch gap-1 flex-1 min-w-0',
            'px-2 py-1.5',
            'overflow-x-auto overscroll-x-contain',
            'snap-x snap-mandatory',
            '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
          )}
        >
          {dockItems.map((item) => {
            const href = item.href || '#'
            const active = isActive(location.pathname, item)
            const Icon = item.icon
            const accent = statusClasses(item, active)
            const className = cn(
              'relative shrink-0',
              'flex flex-col items-center justify-center gap-0.5',
              // Compact: narrower + shorter than before but min-h-[44px] keeps
              // the tap target at the accessible minimum.
              'snap-start w-[64px] min-h-[44px] px-1 py-1 rounded-xl',
              'transition-colors',
              accent,
            )
            // Hub URLs are cross-product fully-qualified URLs
            // (e.g. https://crm.quotech.ai). Use a raw <a href> so
            // the browser performs a full-page navigation — the
            // SSO cookie handles the auth handoff. NAV_ITEMS and
            // SECONDARY_NAV_ITEMS are SPA routes so we keep <Link>
            // for those (no full reload, preserves react-query
            // cache). Mirrors HubLinkWithFlyout (sidebar twin).
            const tileBody = (
              <>
                {Icon ? <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" /> : null}
                <span className="text-[10px] font-medium leading-tight truncate max-w-full">
                  {item.label}
                </span>
                {item.kind === 'hub' && item.status === 'development' && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-0.5 -right-0.5 inline-flex h-2 w-2 rounded-full bg-amber-400"
                    title="In development"
                  />
                )}
                {item.kind === 'hub' && item.status === 'planned' && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-0.5 -right-0.5 inline-flex h-2 w-2 rounded-full bg-slate-400"
                    title="Planned"
                  />
                )}
              </>
            )
            const commonProps = {
              'aria-label': item.label,
              'aria-current': active ? ('page' as const) : undefined,
              'data-active': active ? 'true' : 'false',
              'data-dock-kind': item.kind,
              ...(item.kind === 'hub' && item.status ? { 'data-dock-status': item.status } : {}),
              title: item.label,
              className,
            }
            if (item.kind === 'hub' && isExternalHref(href)) {
              return (
                <a
                  key={item.id}
                  {...commonProps}
                  href={href}
                  onClick={() => {
                    trackDockNav(item.href || item.id)
                    trackNavClick(item.id, dockNavHubId(item.kind, item.id, usageHubId))
                  }}
                >
                  {tileBody}
                </a>
              )
            }
            return (
              <Link
                key={item.id}
                {...commonProps}
                to={href}
                onClick={() => {
                  trackDockNav(item.href || item.id)
                  trackNavClick(item.id, dockNavHubId(item.kind, item.id, usageHubId))
                }}
              >
                {tileBody}
              </Link>
            )
          })}
        </div>
        {canScrollRight && (
          <button
            type="button"
            data-testid="master-mobile-dock-scroll-right"
            onClick={() => scrollDock(1)}
            aria-label="Scroll navigation right"
            className="flex-shrink-0 flex items-center justify-center w-9 my-1.5 mr-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
      </div>
    </>
  )
}

export default MobileDock
